/**
 * 生成服务（P2-T1）：把「能力 → 产物」的调用收口到一个 provider 接口后。
 *
 * 现在只有 mockProvider（返回 model/generation 的内置示例、模拟一点延迟）；
 * 接真实 API 时新增 remoteProvider（POST /api/v2/generate + 轮询 task），
 * 只改文件底部 `generationProvider` 的指向即可，调用方与数据契约一律不动。
 */
import {
  mockLiteraryScript,
  mockStoryboard,
  type GeneratedPayload,
  type LiteraryScript,
  type TextGenCapability,
} from '../../model/generation';
import { turnaroundPrompts, type SubjectPayload } from '../../model/subject';
import { SYSTEM_VOICES, makeCustomVoice, type VoiceProfile } from '../../model/voice';

export interface TextGenRequest {
  capability: TextGenCapability;
  prompt: string;
  /** 导演拆分时可选带上游剧本（沿用标题等） */
  upstreamScript?: LiteraryScript | undefined;
}

/** 图像生成请求（mock 阶段参数与真供商对齐，接真时换实现） */
export interface ImageGenRequest {
  prompt: string;
  aspectRatio: string;
  resolution: string;
  count: number;
}

export interface GeneratedImage {
  url: string;
  thumbnail: string;
  width: number;
  height: number;
}

/** 图像编辑操作（悬停工具栏）：重绘 / 扩图 / 抠图 */
export type ImageEditOp = 'repaint' | 'outpaint' | 'cutout';

/** 视频产物（mock 用占位图作首帧/缩略，url 为视频地址占位） */
export interface GeneratedVideo {
  url: string;
  thumbnail: string;
  durationSec: number;
}

/** 视频工具操作（P-B4）。 */
export type VideoToolOp = 'frame' | 'trim' | 'upscale' | 'desub' | 'translate' | 'separate';

/** 视频工具产物：一个派生节点（video/image/audio）。 */
export interface VideoToolOutput {
  kind: 'video' | 'image' | 'audio';
  url: string;
  thumbnail?: string | undefined;
  label: string;
}

export interface GenerationProvider {
  /** 返回结构化产物；真实实现内部自行处理异步提交与轮询 */
  generate(req: TextGenRequest): Promise<GeneratedPayload>;
  /** 提示词一键优化（行业标配）：真实接入时换旧项目 8 层引擎，现为 mock */
  enhancePrompt(prompt: string): Promise<string>;
  /** 文生图：返回 count 张图（mock 返回内置占位图 data-URI） */
  generateImage(req: ImageGenRequest): Promise<GeneratedImage[]>;
  /** 图像编辑（重绘/扩图/抠图）：mock 返回一张带操作标记的占位图 */
  editImage(op: ImageEditOp, req: { prompt: string; aspectRatio: string; resolution: string }): Promise<GeneratedImage>;
  /** 主体三视图（正/侧/背）：mock 返回 3 张带视角标记的占位图 */
  generateTurnarounds(req: { subject: SubjectPayload; aspectRatio: string; resolution: string }): Promise<GeneratedImage[]>;
  /** 反推提示词：从图片解析生成 prompt（mock 返回占位描述，接真时换 VLM） */
  reversePrompt(req: { imageUrl: string }): Promise<string>;
  /** 高清超分：放大图片（mock 返回带“高清”标记的占位图，接真时换超分模型） */
  upscale(req: { imageUrl: string; resolution: string }): Promise<GeneratedImage>;
  /** 多视角：单图→多角度视图（mock 返回 count 张带视角标记的占位图，接真时换多视角模型） */
  multiView(req: { imageUrl: string; count: number }): Promise<GeneratedImage[]>;
  /** 分镜视频：mock 返回占位首帧 + 时长（接真时换图生视频） */
  generateVideo(req: { prompt: string; aspectRatio: string; resolution: string; durationSec: number }): Promise<GeneratedVideo>;
  /** 视频工具箱（P-B4）：选帧/裁切/高清/去字幕/翻译/音画分离，mock 返回占位产物，接真时挂 /api/v2 */
  editVideo(op: VideoToolOp, req: { videoUrl: string }): Promise<VideoToolOutput[]>;
  /** 音色库：系统可商用音色（接真时并入已授权/已造/已克隆） */
  listVoices(): VoiceProfile[];
  /** 声音设计：文字描述→原创音色（mock 造个占位 profile） */
  designVoice(req: { prompt: string; name: string }): Promise<VoiceProfile>;
  /** 声音复刻：上传有权样本→克隆（mock） */
  cloneVoice(req: { sampleDataUrl: string; name: string }): Promise<VoiceProfile>;
  /** 配音 TTS：按音色 + 情绪合成（mock 返回静音 wav） */
  tts(req: { text: string; voiceId: string; emotion?: string; rate?: number }): Promise<{ url: string; voiceId: string }>;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/* ---------- mock 占位图（data-URI SVG，零依赖、能直接当 <img src>） ---------- */

function ratioDims(ratio: string): { w: number; h: number } {
  const parts = ratio.split(':');
  const a = Number(parts[0]);
  const b = Number(parts[1]);
  const aw = Number.isFinite(a) && a > 0 ? a : 16;
  const bb = Number.isFinite(b) && b > 0 ? b : 9;
  const base = 512;
  return aw >= bb ? { w: base, h: Math.round((base * bb) / aw) } : { w: Math.round((base * aw) / bb), h: base };
}

function hashHue(s: string): number {
  let h = 0;
  for (const c of s) {
    h = (h * 31 + c.charCodeAt(0)) >>> 0;
  }
  return h % 360;
}

function escapeXml(s: string): string {
  return s.replace(/[<>&]/g, (c) => (c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&amp;'));
}

function svgPlaceholder(prompt: string, ratio: string, res: string, i: number): string {
  const { w, h } = ratioDims(ratio);
  const hue = (hashHue(prompt) + i * 40) % 360;
  const label = escapeXml(prompt.trim() === '' ? '（空提示词）' : prompt.trim().slice(0, 14));
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}' viewBox='0 0 ${w} ${h}'>` +
    `<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>` +
    `<stop offset='0' stop-color='hsl(${hue},45%,30%)'/><stop offset='1' stop-color='hsl(${(hue + 50) % 360},50%,16%)'/>` +
    `</linearGradient></defs><rect width='100%' height='100%' fill='url(#g)'/>` +
    `<text x='50%' y='47%' fill='#E8A33D' font-family='sans-serif' font-size='${Math.max(16, Math.round(w / 16))}' text-anchor='middle'>${label}</text>` +
    `<text x='50%' y='61%' fill='#ffffff' fill-opacity='0.6' font-family='sans-serif' font-size='${Math.max(11, Math.round(w / 26))}' text-anchor='middle'>Mock · ${ratio} · ${res}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** Mock：模拟 ~600ms 网络延迟后返回内置结构，用于跑通交互与数据流。 */
export const mockProvider: GenerationProvider = {
  async generate(req) {
    await delay(600);
    if (req.capability === 'textToScript') {
      return { type: 'literaryScript', script: mockLiteraryScript(req.prompt) };
    }
    return { type: 'storyboard', storyboard: mockStoryboard(req.prompt, req.upstreamScript) };
  },
  async enhancePrompt(prompt) {
    await delay(400);
    const p = prompt.trim();
    if (p === '') {
      return p;
    }
    // Mock：追加一段专业修饰词。接真实时换旧项目 prompt 增强引擎。
    return `${p}，电影级质感、光影细腻、构图专业、情绪表达到位`;
  },
  async generateImage(req) {
    await delay(700);
    const n = Math.max(1, Math.min(4, Math.round(req.count) || 1));
    const { w, h } = ratioDims(req.aspectRatio);
    return Array.from({ length: n }, (_, i) => {
      const url = svgPlaceholder(req.prompt, req.aspectRatio, req.resolution, i);
      return { url, thumbnail: url, width: w, height: h };
    });
  },
  async editImage(op, req) {
    await delay(600);
    const label = op === 'repaint' ? '重绘' : op === 'outpaint' ? '扩图' : '抠图';
    const ratio = op === 'outpaint' ? '1:1' : req.aspectRatio; // 扩图：放宽画幅
    const { w, h } = ratioDims(ratio);
    const url = svgPlaceholder(`${label}·${req.prompt}`, ratio, req.resolution, 0);
    return { url, thumbnail: url, width: w, height: h };
  },
  async generateTurnarounds(req) {
    await delay(900);
    const prompts = turnaroundPrompts(req.subject);
    const { w, h } = ratioDims(req.aspectRatio);
    return prompts.map((t, i) => {
      const url = svgPlaceholder(t.prompt, req.aspectRatio, req.resolution, i);
      return { url, thumbnail: url, width: w, height: h };
    });
  },
  async reversePrompt(req) {
    await delay(700);
    void req; // mock 不解析图像，接真时走 VLM 反推
    return '（反推·mock）电影感构图，主体居中，柔和侧逆光，浅景深背景虚化，冷暖对比色调，高细节质感';
  },
  async upscale(req) {
    await delay(800);
    const { w, h } = ratioDims('16:9');
    const url = svgPlaceholder(`高清·${req.resolution}`, '16:9', req.resolution, 0);
    return { url, thumbnail: url, width: w * 2, height: h * 2 };
  },
  async multiView(req) {
    await delay(900);
    const n = Math.max(1, Math.min(6, Math.round(req.count) || 4));
    const { w, h } = ratioDims('1:1');
    return Array.from({ length: n }, (_, i) => {
      const url = svgPlaceholder(`视角${i + 1}`, '1:1', '1K', i);
      return { url, thumbnail: url, width: w, height: h };
    });
  },
  async generateVideo(req) {
    await delay(1200);
    const thumbnail = svgPlaceholder(req.prompt, req.aspectRatio, req.resolution, 0);
    return { url: thumbnail, thumbnail, durationSec: req.durationSec > 0 ? req.durationSec : 5 };
  },
  async editVideo(op) {
    await delay(900);
    if (op === 'frame') {
      const url = svgPlaceholder('选帧', '16:9', '1K', 0);
      return [{ kind: 'image', url, thumbnail: url, label: '选帧' }];
    }
    if (op === 'separate') {
      const still = svgPlaceholder('无声画面', '16:9', '1K', 0);
      return [
        { kind: 'video', url: still, thumbnail: still, label: '无声画面' },
        { kind: 'audio', url: 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=', label: '音频' },
      ];
    }
    const label = op === 'trim' ? '剪辑' : op === 'upscale' ? '高清' : op === 'desub' ? '去字幕' : '翻译';
    const thumb = svgPlaceholder(label, '16:9', '1K', 0);
    return [{ kind: 'video', url: thumb, thumbnail: thumb, label }];
  },
  async tts(req) {
    await delay(600);
    void req; // mock 不消费文本/音色，接真时按 voiceId+emotion→instruct 合成
    // 一段最小合法静音 WAV（44 字节头 + 0 采样）
    return { url: 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=', voiceId: req.voiceId };
  },
  listVoices() {
    return SYSTEM_VOICES;
  },
  async designVoice(req) {
    await delay(800);
    return makeCustomVoice({
      source: 'design',
      voiceId: `design-${Date.now().toString(36)}`,
      name: req.name,
      desc: req.prompt,
    });
  },
  async cloneVoice(req) {
    await delay(900);
    void req.sampleDataUrl; // mock 不建模，接真时走 CosyVoice 声纹注册
    return makeCustomVoice({
      source: 'clone',
      voiceId: `clone-${Date.now().toString(36)}`,
      name: req.name,
    });
  },
};

/** 当前生效的 provider。接真实后端时改成 remoteProvider。 */
export const generationProvider: GenerationProvider = mockProvider;
